import React, { Component } from 'react'
import gql from 'graphql-tag'
import { withApollo } from '@apollo/client/react/hoc'
import { withRouter } from 'react-router-dom'
import { injectIntl, FormattedMessage } from 'react-intl'
import { Modal, Form, Input, Button, Collapse, message } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUsers } from '@fortawesome/free-solid-svg-icons'

import * as Utils from '../../components/Utils'
import PlaylistBrowser from '../../components/PlaylistsBrowser/PlaylistsBrowser'
import { SelectTeamV2 } from '../teams/SelectTeam'
import { SamplesTableQuery, SampleDetailsQuery } from './Queries'

class EditSampleModal extends Component {
	formRef = React.createRef()

	state = {
		loading: false,
		team: this.props.sample?.team ?? null,
		playlistId: this.props.sample?.playlist?._id ?? null,
		edited: false
	}

	save = async () => {
		const { client, history, onClose, sample, duplicate } = this.props
		if (!this.state.team) {
			message.error(
				this.props.intl.formatMessage({ id: 'server.TEAM_NOT_FOUND' })
			)
			return
		}
		if (!duplicate && !this.state.playlistId) {
			message.error(
				this.props.intl.formatMessage({
					id: 'server.PLAYLIST_NOT_FOUND'
				})
			)
			return
		}

		this.setState({ loading: true })

		const values = await this.formRef.current.validateFields()
		try {
			let res
			if (sample && !duplicate) {
				const r = await client.mutate({
					variables: {
						id: sample._id,
						name: values.name,
						teamId: this.state.team._id,
						playlistId: this.state.playlistId
					},
					mutation: gql`
						mutation updateSample(
							$id: String!
							$name: String!
							$teamId: String
							$playlistId: String!
						) {
							sampleUpdateById(
								_id: $id
								name: $name
								teamId: $teamId
								playlistId: $playlistId
							) {
								recordId
							}
						}
					`,
					refetchQueries: [
						{
							query: SamplesTableQuery
						},
						{
							query: SampleDetailsQuery,
							variables: { id: sample._id }
						}
					],
					awaitRefetchQueries: true
				})

				Utils.parseMutationResponse(r)
				res = r.data.sampleUpdateById
			} else if (duplicate) {
				const r = await client.mutate({
					variables: {
						name: values.name,
						teamId: this.state.team._id,
						duplicateId: sample._id
					},
					mutation: gql`
						mutation duplicateSample(
							$name: String!
							$teamId: String!
							$duplicateId: String!
						) {
							sampleDuplicate(
								name: $name
								teamId: $teamId
								duplicateId: $duplicateId
							) {
								recordId
							}
						}
					`,
					refetchQueries: [{ query: SamplesTableQuery }]
				})

				Utils.parseMutationResponse(r)
				res = r.data.sampleDuplicate
			} else {
				const r = await client.mutate({
					variables: {
						name: values.name,
						teamId: this.state.team._id,
						playlistId: this.state.playlistId
					},
					mutation: gql`
						mutation createSample(
							$name: String!
							$teamId: String!
							$playlistId: String!
						) {
							sampleCreate(
								name: $name
								teamId: $teamId
								playlistId: $playlistId
							) {
								recordId
							}
						}
					`,
					refetchQueries: [{ query: SamplesTableQuery }]
				})

				Utils.parseMutationResponse(r)
				res = r.data.sampleCreate
			}

			this.setState({ loading: false })
			if (res) {
				if (res.recordId) {
					// redirect to sample details
					onClose()
					history.push(`/samples/${res.recordId}`)
				} else if (res.message) {
					throw new Error(res.message)
				}
			}
		} catch (e) {
			this.setState({ loading: false })
			Utils.displayError(e)
		}
	}

	render() {
		const { intl, sample, duplicate } = this.props

		let footer = [
			<Button
				type="primary"
				htmlType="submit"
				key="submit"
				onClick={this.save}
				loading={this.state.loading}
				disabled={
					this.state.loading ||
					!this.state.edited ||
					!this.state.playlistId
				}
			>
				<FormattedMessage
					id={sample ? 'actions.save' : 'actions.create'}
				/>
			</Button>
		]

		return (
			<Modal
				visible={true}
				width="80vw"
				title={
					duplicate ?
						intl.formatMessage({
							id: 'samples.actions.duplicateSample'
						})
					: sample ?
						intl.formatMessage(
							{ id: 'samples.actions.editSample' },
							{ name: sample.name }
						)
					:	intl.formatMessage({ id: 'samples.actions.newSample' })
				}
				onCancel={this.props.onClose}
				footer={footer}
			>
				<Form ref={this.formRef}>
					<Form.Item
						name="name"
						initialValue={
							sample &&
							(duplicate ? sample.name + ' copy' : sample.name)
						}
						rules={[
							{
								required: true,
								message: intl.formatMessage({
									id: 'playlists.edit.noName'
								})
							}
						]}
						label={intl.formatMessage({ id: 'name' })}
					>
						<Input
							onChange={() => this.setState({ edited: true })}
						/>
					</Form.Item>

					<Form.Item
						label={
							<React.Fragment>
								<FontAwesomeIcon icon={faUsers} />
								&nbsp;&nbsp;
								<FormattedMessage id="team" />
							</React.Fragment>
						}
					>
						<div data-test="edit-sample-select-team">
							<SelectTeamV2
								onChange={(team) =>
									this.setState({ team, edited: true })
								}
								selected={
									this.state.team ? this.state.team._id : null
								}
								keyprops="_id"
								hideNoParents
							/>
						</div>
					</Form.Item>

					{!duplicate && (
						<Collapse
							className="column-browser"
							accordion
							activeKey="1"
							style={{ width: '100%' }}
						>
							<Collapse.Panel
								header={
									<span>
										<b>
											<FormattedMessage id="playlists.edit.assignPlaylist" />
											:
										</b>
									</span>
								}
								key="1"
							>
								<PlaylistBrowser
									style={{ height: 400 }}
									selectedPath={
										this.props.sample?.playlist?.idPath ??
										[]
									}
									onChange={(playlistId) => {
										if (playlistId) {
											this.setState({
												playlistId: playlistId,
												edited: true
											})
										} else {
											this.setState({
												playlistId: null,
												edited: true
											})
										}
									}}
								/>
							</Collapse.Panel>
						</Collapse>
					)}
				</Form>
			</Modal>
		)
	}
}

export default withRouter(withApollo(injectIntl(EditSampleModal)))
